import { useNavigate, useLocation } from 'react-router-dom';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  LayoutDashboard,
  Users,
  Activity,
  BarChart3,
  Users2,
  DollarSign,
  ServerCrash,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  ArrowLeft,
  Crown,
  ToggleLeft,
  ShieldOff,
  Shield,
  FileText,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { CleviaLogo } from '@/components/CleviaLogo';

interface AdminSidebarStore {
  collapsed: boolean;
  toggleCollapsed: () => void;
}

export const useAdminSidebar = create<AdminSidebarStore>()(
  persist(
    (set) => ({
      collapsed: false,
      toggleCollapsed: () => set((s) => ({ collapsed: !s.collapsed })),
    }),
    { name: 'clevia-admin-sidebar' }
  )
);

interface AdminFilterStore {
  excludeAdmins: boolean;
  toggleExcludeAdmins: () => void;
}

export const useAdminFilter = create<AdminFilterStore>()(
  persist(
    (set) => ({
      excludeAdmins: true,
      toggleExcludeAdmins: () => set((s) => ({ excludeAdmins: !s.excludeAdmins })),
    }),
    { name: 'clevia-admin-filter' }
  )
);

export function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, adminRole } = useAuth();
  const { collapsed, toggleCollapsed } = useAdminSidebar();
  const { excludeAdmins, toggleExcludeAdmins } = useAdminFilter();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const navItems = [
    { id: 'overview', icon: LayoutDashboard, label: 'Global Overview', path: '/admin' },
    { id: 'users', icon: Users, label: 'User Growth', path: '/admin/users' },
    { id: 'usage', icon: BarChart3, label: 'Product Usage', path: '/admin/usage' },
    { id: 'engagement', icon: Activity, label: 'Engagement', path: '/admin/engagement' },
    { id: 'referral', icon: Users2, label: 'Referral', path: '/admin/referral' },
    { id: 'financial', icon: DollarSign, label: 'Financial', path: '/admin/financial' },
    { id: 'status', icon: ServerCrash, label: 'Status', path: '/admin/status' },
    { id: 'features', icon: ToggleLeft, label: 'Feature Flags', path: '/admin/features' },
    { id: 'pages', icon: FileText, label: 'CMS Pages', path: '/admin/pages' },
    ...(adminRole === 'superadmin'
      ? [{ id: 'team', icon: Crown, label: 'Gestion admins', path: '/admin/team' }]
      : []),
  ];

  const currentPage = (() => {
    const path = location.pathname;
    if (path === '/admin' || path === '/admin/') return 'overview';
    const match = navItems.find((item) => item.path !== '/admin' && path.startsWith(item.path));
    return match?.id ?? 'overview';
  })();

  return (
    <aside
      className={`fixed left-0 top-0 h-full ${collapsed ? 'w-[72px]' : 'w-64'} bg-[#f8fafb] dark:bg-gray-800 border-r border-gray-100 dark:border-gray-700/50 z-50 hidden lg:flex flex-col transition-all duration-200`}
    >
      {/* Logo + Admin badge */}
      <div
        className={`${collapsed ? 'px-3' : 'px-6'} py-6 border-b border-gray-100 dark:border-gray-700`}
      >
        <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          <div className="cursor-pointer group" onClick={() => navigate('/admin')}>
            {collapsed ? (
              <span className="text-xl font-bold text-[#16c47f]">c</span>
            ) : (
              <div className="flex items-center gap-2">
                <CleviaLogo
                  size="sm"
                  className="group-hover:scale-105 transition-transform duration-200"
                />
                <span className="text-[10px] font-bold uppercase tracking-widest bg-[#16c47f]/10 text-[#16c47f] px-2 py-0.5 rounded-md">
                  Admin
                </span>
              </div>
            )}
          </div>
          {!collapsed && (
            <button
              onClick={toggleCollapsed}
              className="p-2 rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
            >
              <PanelLeftClose className="w-4 h-4" strokeWidth={2} />
            </button>
          )}
        </div>
      </div>

      {/* Expand button when collapsed */}
      {collapsed && (
        <div className="px-3 pt-3">
          <button
            onClick={toggleCollapsed}
            className="w-full flex items-center justify-center p-2.5 rounded-xl text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <PanelLeft className="w-[18px] h-[18px]" strokeWidth={2} />
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === currentPage;

          return (
            <button
              key={item.id}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium transition-all duration-150 ${
                isActive
                  ? 'bg-[#16c47f]/10 text-[#16c47f]'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100/80 dark:hover:bg-gray-700/50'
              }`}
              title={collapsed ? item.label : undefined}
            >
              <Icon
                className={`w-[18px] h-[18px] flex-shrink-0 transition-colors ${
                  isActive ? 'text-[#16c47f]' : 'text-gray-400 dark:text-gray-500'
                }`}
                strokeWidth={isActive ? 2.5 : 2}
              />
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && isActive && (
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#16c47f]" />
              )}
            </button>
          );
        })}
      </nav>

      {/* Admin filter toggle */}
      <div className="px-3 py-3 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={toggleExcludeAdmins}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium transition-all duration-150 ${
            excludeAdmins
              ? 'bg-[#16c47f]/10 text-[#16c47f]'
              : 'bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400'
          }`}
          title={collapsed ? (excludeAdmins ? 'Sans admins' : 'Avec admins') : undefined}
        >
          {excludeAdmins ? (
            <ShieldOff className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          ) : (
            <Shield className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          )}
          {!collapsed && <span>{excludeAdmins ? 'Sans admins' : 'Avec admins'}</span>}
        </button>
      </div>

      {/* Bottom actions */}
      <div className="px-3 pb-5 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-0.5">
        {/* Back to clevia */}
        <button
          onClick={() => navigate('/dashboard')}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-[#16c47f] hover:bg-[#16c47f]/5 transition-all duration-150`}
          title={collapsed ? 'Back to clevia' : undefined}
        >
          <ArrowLeft className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          {!collapsed && <span>Back to clevia</span>}
        </button>
        {/* Logout */}
        <button
          onClick={handleLogout}
          className={`w-full flex items-center ${collapsed ? 'justify-center' : ''} gap-3 ${collapsed ? 'px-0 py-2.5' : 'px-3 py-2.5'} rounded-xl text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all duration-150`}
          title={collapsed ? 'Déconnexion' : undefined}
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={2} />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </div>
    </aside>
  );
}
